import React, { useEffect, useRef, useState, useCallback } from "react";
import HostRequestDetails from "./HostRequestDetails";
import CCService from "../../services/httpServiceWithoutSuperAdmin";

export default function HostRequest() {
  const service = useRef(new CCService()).current;

  const [items, setItems] = useState([]); // list of loaded items
  const [nextCursor, setNextCursor] = useState(null); // cursor for next page
  const [isFetching, setIsFetching] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState("");
  const [hasMore, setHasMore] = useState(true);

  // sentinel ref observed by IntersectionObserver
  const sentinelRef = useRef(null);

  // fetch page function
  const fetchPage = useCallback(
    async (cursor = null) => {
      // prevent concurrent fetches
      if (isFetching) return;
      setIsFetching(true);
      setError("");

      try {
        const params = new URLSearchParams();
        params.append("limit", "10");
        if (cursor) params.append("cursor", cursor);

        // adjust path if needed — we expect { data: [...], nextCursor }
        const res = await service.get(
          `/ahead/organising-partners?${params.toString()}`
        );

        // normalize response shape: body.data should be array
        const pageItems = Array.isArray(res.data)
          ? res.data
          : res.data
          ? [res.data]
          : [];
        const pageCursor = res.nextCursor ?? null;

        setItems((prev) => {
          // avoid duplicates (in case API returns same items)
          const existingIds = new Set(prev.map((i) => i.id));
          const appended = pageItems.filter((p) => !existingIds.has(p.id));
          return prev.concat(appended);
        });

        setNextCursor(pageCursor);
        setHasMore(Boolean(pageCursor)); // if no cursor => no more pages
      } catch (err) {
        console.error("Failed to fetch host requests:", err);
        setError(err?.message || "Failed to load host requests");
      } finally {
        setIsFetching(false);
        setInitialLoading(false);
      }
    },
    [isFetching, service]
  );

  // initial load
  useEffect(() => {
    fetchPage(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once

  // intersection observer to trigger loading next page
  useEffect(() => {
    if (!sentinelRef.current) return;
    if (!hasMore) return; // nothing to load

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isFetching && hasMore) {
            // load next page
            fetchPage(nextCursor);
          }
        });
      },
      {
        root: null, // viewport
        rootMargin: "200px", // prefetch a bit before reaching sentinel
        threshold: 0.1,
      }
    );

    observer.observe(sentinelRef.current);

    return () => {
      observer.disconnect();
    };
  }, [fetchPage, isFetching, nextCursor, hasMore]);

  console.log("items", items);
  // UI states
  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-10 px-4 flex items-center justify-center">
        <div className="text-gray-500">Loading host requests…</div>
      </div>
    );
  }

  if (error && items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-10 px-4 flex items-center justify-center">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-6">
        Organising Partner Request
      </h2>

      <div className="max-w-6xl mx-auto space-y-4">
        {items.map((item) => (
          <HostRequestDetails key={item.id} data={item} />
        ))}

        {/* sentinel: when visible, loads next page */}
        <div ref={sentinelRef} style={{ height: 1 }} />

        {/* loader / end of list */}
        <div className="text-center py-6">
          {isFetching && <div className="text-gray-500">Loading more…</div>}
          {!hasMore && !isFetching && items.length > 0 && (
            <div className="text-gray-400">No more host requests.</div>
          )}
          {error && items.length > 0 && (
            <div className="text-red-600">{error}</div>
          )}
        </div>
      </div>
    </div>
  );
}
